import { createHash } from 'crypto';
import fs from 'fs-extra';
import { join } from 'path';
import type { CacheInfo } from 'synthia-core/shared';

export class HashManager {
  private hashAlgorithm = 'sha256';

  constructor(_cacheDir: string = '.synthia-cache') {
    // cacheDir is not used in this implementation
  }

  /**
   * 计算文件哈希
   */
  calculateFileHash(filePath: string): string {
    try {
      const content = fs.readFileSync(filePath);
      const hash = createHash(this.hashAlgorithm);
      hash.update(content);
      return hash.digest('hex');
    } catch (error) {
      console.warn(`无法计算文件哈希: ${filePath}`, error);
      return '';
    }
  }

  /**
   * 计算目录哈希
   */
  calculateDirectoryHash(
    dirPath: string,
    patterns: string[] = ['**/*']
  ): string {
    const hash = createHash(this.hashAlgorithm);

    try {
      const files = this.getFilesInDirectory(dirPath, patterns);
      files.sort(); // 确保顺序一致

      for (const file of files) {
        const filePath = join(dirPath, file);
        const stats = fs.statSync(filePath);
        const content = fs.readFileSync(filePath);

        // 包含文件路径、大小、修改时间和内容
        hash.update(file);
        hash.update(stats.size.toString());
        hash.update(stats.mtime.getTime().toString());
        hash.update(content);
      }

      return hash.digest('hex');
    } catch (error) {
      console.warn(`无法计算目录哈希: ${dirPath}`, error);
      return '';
    }
  }

  /**
   * 计算依赖哈希
   */
  calculateDependenciesHash(dependencies: Record<string, string>): string {
    const hash = createHash(this.hashAlgorithm);

    // 按key排序确保一致性
    const sortedDeps = Object.keys(dependencies).sort();

    for (const key of sortedDeps) {
      hash.update(key);
      hash.update(dependencies[key]);
    }

    return hash.digest('hex');
  }

  /**
   * 生成缓存键
   */
  generateCacheKey(
    projectPath: string,
    dependencies: Record<string, string>,
    buildConfig: Record<string, any>
  ): string {
    const hash = createHash(this.hashAlgorithm);

    // 项目路径哈希
    const projectHash = this.calculateDirectoryHash(projectPath);
    hash.update(projectHash);

    // 依赖哈希
    const depsHash = this.calculateDependenciesHash(dependencies);
    hash.update(depsHash);

    // 构建配置哈希
    const configHash = this.calculateObjectHash(buildConfig);
    hash.update(configHash);

    return hash.digest('hex');
  }

  /**
   * 创建缓存信息
   */
  createCacheInfo(
    hash: string,
    size: number,
    dependencies: string[]
  ): CacheInfo {
    return {
      hash,
      size,
      lastModified: Date.now(),
      dependencies,
    };
  }

  /**
   * 验证缓存有效性
   */
  validateCache(cacheInfo: CacheInfo, currentHash: string): boolean {
    return cacheInfo.hash === currentHash;
  }

  /**
   * 获取目录中的文件
   */
  private getFilesInDirectory(dirPath: string, patterns: string[]): string[] {
    const { glob } = require('glob');
    const files: string[] = [];

    for (const pattern of patterns) {
      const matches = glob.sync(pattern, {
        cwd: dirPath,
        nodir: true,
        ignore: ['node_modules/**', '.git/**', 'dist/**', 'build/**'],
      });
      files.push(...matches);
    }

    return [...new Set(files)]; // 去重
  }

  /**
   * 计算对象哈希
   */
  private calculateObjectHash(obj: any): string {
    const hash = createHash(this.hashAlgorithm);
    const str = JSON.stringify(obj, Object.keys(obj).sort());
    hash.update(str);
    return hash.digest('hex');
  }
}
